<script setup>
/**
 * SalesList Component
 * Displays a list/table of sales promotions for a selected merakez
 */
import { ref, computed, watch } from 'vue'
import { formatDate } from '@core/utils/formatting.js'

const props = defineProps({
  sales: {
    type: Array,
    default: () => [],
  },
  selectedMerakez: {
    type: String,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
  exporting: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['create', 'edit', 'delete', 'duplicate', 'export'])

// ============================================
// Filters State
// ============================================
const hideInactive = ref(true)
const hideExpired = ref(true)
const hideNewMembers = ref(false)
const searchQuery = ref('')

// ============================================
// Filtered Sales
// ============================================
const filteredSales = computed(() => {
  let result = props.sales

  // Filter out inactive sales
  if (hideInactive.value) {
    result = result.filter((sale) => sale.active === true)
  }

  // Filter out expired sales
  if (hideExpired.value) {
    const now = new Date()
    result = result.filter((sale) => {
      if (!sale.end_date && !sale.date_end_date) return true
      // Handle Firestore timestamp
      if (sale.date_end_date?.toDate) {
        return sale.date_end_date.toDate() >= now
      }
      // Handle string date
      if (sale.end_date) {
        const endDate = new Date(sale.end_date)
        return endDate >= now
      }
      return true
    })
  }

  // Filter out new members only promotions
  if (hideNewMembers.value) {
    result = result.filter((sale) => sale.first_order !== true)
  }

  // Search filter
  const q = searchQuery.value.trim().toLowerCase()
  if (q) {
    result = result.filter((sale) => {
      // Match promotion label
      if ((sale.label || '').toLowerCase().includes(q)) return true
      // Match item makats or names
      if (sale.makats_arr?.some((m) => m.toLowerCase().includes(q))) return true
      if (
        sale.items?.some(
          (item) =>
            (item.makat || '').toLowerCase().includes(q) ||
            (item.name || '').toLowerCase().includes(q)
        )
      )
        return true
      return false
    })
  }

  return result
})

// ============================================
// Selection State (for export)
// ============================================
const selectedIds = ref(new Set())

function isSelected(id) {
  return selectedIds.value.has(id)
}

function toggleSelect(id) {
  if (selectedIds.value.has(id)) {
    selectedIds.value.delete(id)
  } else {
    selectedIds.value.add(id)
  }
}

const allSelected = computed(
  () =>
    filteredSales.value.length > 0 &&
    filteredSales.value.every((sale) => selectedIds.value.has(sale.id))
)

const someSelected = computed(() => selectedIds.value.size > 0 && !allSelected.value)

function toggleSelectAll() {
  if (allSelected.value) {
    selectedIds.value.clear()
  } else {
    filteredSales.value.forEach((sale) => selectedIds.value.add(sale.id))
  }
}

function clearSelection() {
  selectedIds.value.clear()
}

// Reset selection on merakez change; drop ids that are no longer visible after filtering
watch(() => props.selectedMerakez, clearSelection)
watch(filteredSales, (list) => {
  const visible = new Set(list.map((sale) => sale.id))
  for (const id of [...selectedIds.value]) {
    if (!visible.has(id)) selectedIds.value.delete(id)
  }
})

function handleExport() {
  if (selectedIds.value.size === 0 || props.exporting) return
  emit('export', [...selectedIds.value])
}

// Get representative image for a sale
function getSaleImage(sale) {
  if (sale.image) return sale.image
  if (sale.items?.length && sale.items[0].image) return sale.items[0].image
  return ''
}

// Get sale type display label
function getSaleTypeLabel(saleType) {
  const types = {
    madregot: 'מדרגות',
    percentage: 'אחוז הנחה',
    fixed: 'סכום קבוע',
    bundle: 'חבילה',
  }
  return types[saleType] || saleType || '—'
}
</script>

<template>
  <div class="sales-list">
    <!-- Header -->
    <div class="list-header">
      <div class="header-top">
        <h2 class="list-title">
          <template v-if="selectedMerakez">
            מבצעים עבור מרכז: <span class="merakez-badge">{{ selectedMerakez }}</span>
          </template>
          <template v-else> מבצעים </template>
        </h2>
        <div class="header-actions">
          <span v-if="selectedMerakez && !loading && !error" class="list-count">
            {{ filteredSales.length }} / {{ sales.length }} מבצעים
          </span>
          <template v-if="selectedIds.size > 0">
            <button class="clear-selection-btn" :disabled="exporting" @click="clearSelection">
              נקה בחירה
            </button>
            <button class="export-btn" :disabled="exporting" @click="handleExport">
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              <template v-if="exporting">מייצא...</template>
              <template v-else>ייצוא לאקסל ({{ selectedIds.size }})</template>
            </button>
          </template>
          <button
            v-if="selectedMerakez && !loading && !error"
            class="create-btn"
            @click="emit('create')"
          >
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            מבצע חדש
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div v-if="selectedMerakez && !loading && !error" class="header-filters">
        <div class="filter-search">
          <svg
            class="filter-search-icon"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            class="filter-search-input"
            placeholder="חיפוש לפי שם מבצע, מקט או שם פריט..."
          />
        </div>
        <label class="filter-checkbox">
          <input type="checkbox" v-model="hideInactive" />
          <span class="checkbox-label">הסתר לא פעילים</span>
        </label>
        <label class="filter-checkbox">
          <input type="checkbox" v-model="hideExpired" />
          <span class="checkbox-label">הסתר פגי תוקף</span>
        </label>
        <label class="filter-checkbox">
          <input type="checkbox" v-model="hideNewMembers" />
          <span class="checkbox-label">הסתר חברים חדשים</span>
        </label>
      </div>
    </div>

    <!-- No Selection State -->
    <div v-if="!selectedMerakez" class="list-empty-state">
      <div class="empty-icon">
        <svg
          viewBox="0 0 24 24"
          width="48"
          height="48"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
        </svg>
      </div>
      <p class="empty-text">בחר לקוח מרכז כדי להציג מבצעים</p>
    </div>

    <!-- Loading State -->
    <div v-else-if="loading" class="list-loading">
      <div class="loading-spinner"></div>
      <span>טוען מבצעים...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="list-error">
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
      <span>{{ error }}</span>
    </div>

    <!-- Empty Sales State -->
    <div v-else-if="filteredSales.length === 0" class="list-empty-state">
      <div class="empty-icon">
        <svg
          viewBox="0 0 24 24"
          width="48"
          height="48"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
      </div>
      <p class="empty-text">
        <template v-if="sales.length === 0">אין מבצעים עבור מרכז זה</template>
        <template v-else>אין מבצעים התואמים את הסינון</template>
      </p>
    </div>

    <!-- Sales Table (Desktop) -->
    <div v-else class="list-content">
      <table class="sales-table desktop-only">
        <thead>
          <tr>
            <th class="col-select">
              <input
                type="checkbox"
                class="row-checkbox"
                title="בחר הכל"
                :checked="allSelected"
                :indeterminate="someSelected"
                @change="toggleSelectAll"
              />
            </th>
            <th class="col-actions">פעולות</th>
            <th class="col-status">סטטוס</th>
            <th class="col-image">תמונה</th>
            <th class="col-label">שם המבצע</th>
            <th class="col-description">תיאור</th>
            <th class="col-dates">תאריכים</th>
            <th class="col-items">פריטים</th>
            <th class="col-type">סוג</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="sale in filteredSales"
            :key="sale.id"
            class="sale-row"
            :class="{ 'first-order-row': sale.first_order }"
          >
            <!-- Select -->
            <td class="col-select" @click.stop>
              <input
                type="checkbox"
                class="row-checkbox"
                :checked="isSelected(sale.id)"
                @change="toggleSelect(sale.id)"
              />
            </td>

            <!-- Actions -->
            <td class="col-actions">
              <div class="row-actions">
                <button class="action-btn edit-btn" title="עריכה" @click="emit('edit', sale.id)">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  class="action-btn delete-btn"
                  title="מחיקה"
                  @click="emit('delete', sale.id)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                    />
                  </svg>
                </button>
                <button
                  class="action-btn duplicate-btn"
                  title="שכפול למרכז אחר"
                  @click="emit('duplicate', sale.id)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
            </td>

            <!-- Status Badges -->
            <td class="col-status">
              <div class="status-badges">
                <span class="badge" :class="sale.approved ? 'badge-success' : 'badge-warning'">
                  {{ sale.approved ? 'מאושר' : 'ממתין' }}
                </span>
                <span class="badge" :class="sale.active ? 'badge-active' : 'badge-inactive'">
                  {{ sale.active ? 'פעיל' : 'לא פעיל' }}
                </span>
              </div>
            </td>

            <!-- Image -->
            <td class="col-image">
              <img
                v-if="getSaleImage(sale)"
                :src="getSaleImage(sale)"
                :alt="sale.label || ''"
                class="sale-thumb"
              />
              <div v-else class="sale-thumb-empty">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              </div>
            </td>

            <!-- Label -->
            <td class="col-label">
              <div class="label-wrapper">
                <span v-if="sale.first_order" class="first-order-tag">מצטרפים חדשים</span>
                <span v-if="sale.weeklyBenefit" class="weekly-benefit-tag">הטבה שבועית</span>
                <span class="sale-label">{{ sale.label || '—' }}</span>
              </div>
            </td>

            <!-- Description -->
            <td class="col-description">
              <span class="sale-description">{{ sale.description || '—' }}</span>
            </td>

            <!-- Dates -->
            <td class="col-dates">
              <div class="date-range">
                <span>{{ formatDate(sale.start_date) }}</span>
                <span class="date-separator">←</span>
                <span>{{ formatDate(sale.end_date) }}</span>
              </div>
            </td>

            <!-- Items Count -->
            <td class="col-items">
              <span class="items-count">{{ sale.itemsCount }}</span>
            </td>

            <!-- Sale Type -->
            <td class="col-type">
              <span class="sale-type">{{ getSaleTypeLabel(sale.saleType) }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Sales Cards (Mobile) -->
      <div class="sales-cards mobile-only">
        <div
          v-for="sale in filteredSales"
          :key="sale.id"
          class="sale-card"
          :class="{ 'first-order-card': sale.first_order }"
          @click="emit('edit', sale.id)"
        >
          <div class="card-header">
            <div class="card-header-start">
              <input
                type="checkbox"
                class="row-checkbox"
                :checked="isSelected(sale.id)"
                @click.stop
                @change="toggleSelect(sale.id)"
              />
              <div class="card-badges">
                <span class="badge" :class="sale.approved ? 'badge-success' : 'badge-warning'">
                  {{ sale.approved ? 'מאושר' : 'ממתין' }}
                </span>
                <span class="badge" :class="sale.active ? 'badge-active' : 'badge-inactive'">
                  {{ sale.active ? 'פעיל' : 'לא פעיל' }}
                </span>
              </div>
            </div>
            <div class="card-actions">
              <button class="action-btn edit-btn" title="עריכה" @click.stop="emit('edit', sale.id)">
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
              <button
                class="action-btn delete-btn"
                title="מחיקה"
                @click.stop="emit('delete', sale.id)"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                  />
                </svg>
              </button>
              <button
                class="action-btn duplicate-btn"
                title="שכפול למרכז אחר"
                @click.stop="emit('duplicate', sale.id)"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </button>
            </div>
          </div>

          <div class="card-body">
            <div class="card-body-inner">
              <img
                v-if="getSaleImage(sale)"
                :src="getSaleImage(sale)"
                :alt="sale.label || ''"
                class="card-thumb"
              />
              <div class="card-body-text">
                <span v-if="sale.first_order" class="first-order-tag">מצטרפים חדשים</span>
                <span v-if="sale.weeklyBenefit" class="weekly-benefit-tag">הטבה שבועית</span>
                <div class="card-title">{{ sale.label || '—' }}</div>
                <div v-if="sale.description" class="card-description">{{ sale.description }}</div>
              </div>
            </div>
          </div>

          <div class="card-footer">
            <div class="card-meta">
              <span class="meta-item">
                <svg
                  viewBox="0 0 24 24"
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {{ formatDate(sale.start_date) }} ← {{ formatDate(sale.end_date) }}
              </span>
            </div>
            <div class="card-stats">
              <span class="stat-item">{{ sale.itemsCount }} פריטים</span>
              <span class="stat-divider">•</span>
              <span class="stat-item">{{ getSaleTypeLabel(sale.saleType) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sales-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
}

/* Header */
.list-header {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.create-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  color: white;
  background: var(--color-primary);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.create-btn:hover {
  opacity: 0.9;
}

.export-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  color: var(--color-info);
  background: transparent;
  border: 1px solid var(--color-info);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.export-btn:hover:not(:disabled) {
  background: rgba(59, 130, 246, 0.08);
}

.export-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.clear-selection-btn {
  padding: 8px 4px;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-muted);
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}

.clear-selection-btn:hover:not(:disabled) {
  color: var(--text-primary);
  text-decoration: underline;
}

.clear-selection-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Filters */
.header-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-md);
}

.filter-search {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: border-color var(--transition-fast);
  flex: 1;
  min-width: 180px;
  max-width: 300px;
}

.filter-search:focus-within {
  border-color: var(--color-primary);
}

.filter-search-icon {
  color: var(--text-muted);
  flex-shrink: 0;
}

.filter-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  outline: none;
  min-width: 0;
}

.filter-search-input::placeholder {
  color: var(--text-muted);
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.filter-checkbox input[type='checkbox'] {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: var(--color-primary);
}

.checkbox-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.list-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
}

.merakez-badge {
  font-family: var(--font-family-mono, monospace);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
}

.list-count {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

/* Empty State */
.list-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: var(--spacing-2xl);
  text-align: center;
}

.empty-icon {
  color: var(--text-muted);
  opacity: 0.4;
  margin-bottom: var(--spacing-md);
}

.empty-text {
  font-size: var(--font-size-base);
  color: var(--text-muted);
  margin: 0;
}

/* Loading */
.list-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: var(--spacing-sm);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error */
.list-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  text-align: center;
}

/* Table Content */
.list-content {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.sales-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.sales-table th {
  position: sticky;
  top: 0;
  background: var(--bg-secondary);
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: right;
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.sales-table td {
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color-light, var(--border-color));
  vertical-align: middle;
}

.sale-row:hover {
  background: var(--bg-secondary);
}

.first-order-row {
  background: rgba(251, 191, 36, 0.08);
}

.first-order-row:hover {
  background: rgba(251, 191, 36, 0.15);
}

/* Status Badges */
.status-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-full);
  text-align: center;
}

.badge-success {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.badge-warning {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.badge-active {
  background: rgba(99, 102, 241, 0.1);
  color: var(--color-primary);
}

.badge-inactive {
  background: var(--bg-tertiary);
  color: var(--text-muted);
}

/* Columns */
.col-select {
  width: 40px;
  text-align: center;
}

.row-checkbox {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: var(--color-primary);
  vertical-align: middle;
}

.col-actions {
  width: 110px;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.action-btn:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

.edit-btn:hover {
  color: var(--color-primary);
  border-color: var(--color-primary);
}

.delete-btn:hover {
  color: var(--color-error);
  border-color: var(--color-error);
}

.duplicate-btn:hover {
  color: #0ea5e9;
  border-color: #0ea5e9;
}

.col-status {
  width: 90px;
}

.col-image {
  width: 60px;
  text-align: center;
}

.sale-thumb {
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.sale-thumb-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius);
  border: 1px dashed var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-muted);
  opacity: 0.4;
}

.col-label {
  min-width: 150px;
}

.label-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.first-order-tag {
  font-size: 10px;
  color: var(--text-muted);
  font-weight: var(--font-weight-normal);
}

.weekly-benefit-tag {
  font-size: 10px;
  color: var(--color-info);
  font-weight: var(--font-weight-medium);
}

.sale-label {
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.col-description {
  max-width: 250px;
}

.sale-description {
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.col-dates {
  min-width: 180px;
}

.date-range {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--text-secondary);
  font-family: var(--font-family-mono, monospace);
  font-size: 12px;
}

.date-separator {
  color: var(--text-muted);
}

.col-items {
  width: 70px;
  text-align: center;
}

.items-count {
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.col-type {
  width: 100px;
}

.sale-type {
  color: var(--text-secondary);
}

/* Desktop/Mobile visibility */
.mobile-only {
  display: none;
}

.desktop-only {
  display: table;
}

/* Responsive */
@media (max-width: 900px) {
  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: flex;
  }

  .header-top {
    flex-wrap: wrap;
  }

  .header-filters {
    gap: var(--spacing-sm);
  }

  .filter-checkbox {
    flex: 0 0 auto;
  }

  .checkbox-label {
    font-size: 12px;
  }
}

/* Mobile Cards */
.sales-cards {
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
}

.sale-card {
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.sale-card:active {
  transform: scale(0.99);
}

.first-order-card {
  background: rgba(251, 191, 36, 0.08);
  border-color: rgba(251, 191, 36, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color-light, var(--border-color));
  background: var(--bg-secondary);
  border-radius: var(--border-radius) var(--border-radius) 0 0;
}

.card-header-start {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.card-badges {
  display: flex;
  gap: 6px;
}

.card-actions {
  display: flex;
  gap: 4px;
}

.card-body {
  padding: var(--spacing-md);
}

.card-body-inner {
  display: flex;
  gap: var(--spacing-md);
  align-items: flex-start;
}

.card-thumb {
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  flex-shrink: 0;
}

.card-body-text {
  flex: 1;
  min-width: 0;
}

.card-body-text .first-order-tag,
.card-body-text .weekly-benefit-tag {
  display: inline-block;
  margin-bottom: 4px;
  margin-inline-end: 6px;
}

.card-title {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  color: var(--text-primary);
}

.card-description {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin-top: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) var(--spacing-md);
  border-top: 1px solid var(--border-color-light, var(--border-color));
  background: var(--bg-secondary);
  border-radius: 0 0 var(--border-radius) var(--border-radius);
}

.card-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--text-muted);
  font-family: var(--font-family-mono, monospace);
}

.meta-item svg {
  flex-shrink: 0;
}

.card-stats {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-secondary);
}

.stat-divider {
  color: var(--text-muted);
}
</style>
